import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/* ══════════════════════════════════════════════
   LANDING VARIANT D — "Editorial Journal"
   מגזין נייר: קרם, סריף כבד (Frank Ruhl Libre / Suez One),
   קו-מרקר מצויר שנמתח בגלילה, טיקר מילים ענק,
   צילומי מסך כהדפסים מוטים עם נייר-דבק.
   ══════════════════════════════════════════════ */

const SERIF = "'Frank Ruhl Libre', 'Noto Serif Hebrew', serif";
const DISPLAY = "'Suez One', 'Frank Ruhl Libre', serif";
const INK = '#161412';
const CREAM = '#FAF6EE';
const RED = '#D64533';

function useReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(el); } },
      { threshold, rootMargin: '9999px 0px 0px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const TICKER = ['תרומות', 'קמפיינים', 'CRM', 'דיוור', 'WhatsApp', 'תקציב', 'דוחות', 'מתנדבים', 'AI'];

/* masking-tape strip */
const Tape: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <span
    aria-hidden="true"
    style={{
      position: 'absolute', width: 110, height: 30, background: 'rgba(232,214,160,0.85)',
      boxShadow: '0 1px 4px rgba(0,0,0,0.12)', transform: 'rotate(-4deg)', zIndex: 3, ...style,
    }}
  />
);

const LandingVariantD: React.FC = () => {
  const prints = useReveal(0.1);
  const steps = useReveal(0.15);
  const cta = useReveal(0.2);

  return (
    <div dir="rtl" className="min-h-screen overflow-x-clip" style={{ background: CREAM, color: INK, fontFamily: SERIF }}>
      <style>{`
        @keyframes dDraw { to { stroke-dashoffset: 0; } }
        @keyframes dTicker { from { transform: translateX(0); } to { transform: translateX(50%); } }
        @keyframes dRise { from { opacity: 0; transform: translateY(26px); } to { opacity: 1; transform: translateY(0); } }
        .d-rise { animation: dRise 0.9s cubic-bezier(0.22,1,0.36,1) both; }
        .d-d1 { animation-delay: 150ms; } .d-d2 { animation-delay: 300ms; } .d-d3 { animation-delay: 450ms; }
        .d-print { transition: transform 0.6s cubic-bezier(0.22,1,0.36,1), box-shadow 0.6s; }
        .d-print:hover { transform: rotate(0deg) scale(1.02) !important; box-shadow: 0 30px 60px -20px rgba(22,20,18,0.35) !important; z-index: 20 !important; }
      `}</style>

      {/* grain overlay */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-50 opacity-[0.05]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")",
        }}
      />

      {/* ── Thin editorial header ── */}
      <header className="sticky top-0 z-40 border-b" style={{ background: CREAM, borderColor: INK }}>
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="text-2xl" style={{ fontFamily: DISPLAY }}>עמוטק</Link>
          <div className="hidden sm:block text-xs tracking-widest" style={{ fontFamily: 'Heebo, sans-serif' }}>
            כתב־עת לניהול עמותות · גיליון 2026
          </div>
          <div className="flex items-center gap-4" style={{ fontFamily: 'Heebo, sans-serif' }}>
            <Link to="/login" className="text-sm font-semibold hover:underline underline-offset-4">כניסה</Link>
            <Link
              to="/register"
              className="text-sm font-bold px-4 py-2 border-2 transition-colors duration-200"
              style={{ borderColor: INK, background: INK, color: CREAM }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = RED; (e.currentTarget as HTMLElement).style.borderColor = RED; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = INK; (e.currentTarget as HTMLElement).style.borderColor = INK; }}
            >
              התחל בחינם
            </Link>
          </div>
        </div>
      </header>

      {/* ━━━━ HERO ━━━━ */}
      <section className="relative px-6 pt-16 pb-10">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1fr_auto] gap-10 items-start">
          <div>
            <p className="d-rise text-sm font-bold tracking-[0.25em] mb-6" style={{ fontFamily: 'Heebo, sans-serif', color: RED }}>
              — מערכת אחת. עמותה אחת. סדר אחד. —
            </p>
            <h1 className="d-rise d-d1 leading-[1.05]" style={{ fontFamily: DISPLAY, fontSize: 'clamp(44px, 8vw, 104px)' }}>
              לנהל עמותה
              <br />
              <span className="relative inline-block">
                זה לא צריך
                {/* hand-drawn marker underline, draws itself */}
                <svg className="absolute right-0 -bottom-3 w-full" height="14" viewBox="0 0 400 14" preserveAspectRatio="none" aria-hidden="true">
                  <path d="M4,10 C80,4 180,12 250,7 C310,3 370,9 396,6" stroke={RED} strokeWidth="6" fill="none" strokeLinecap="round"
                    strokeDasharray="420" strokeDashoffset="420" style={{ animation: 'dDraw 1.2s 0.9s ease-out forwards' }} />
                </svg>
              </span>
              {' '}לכאוב.
            </h1>
            <p className="d-rise d-d2 mt-8 text-xl leading-relaxed max-w-xl" style={{ fontFamily: 'Heebo, sans-serif', color: '#4a4540' }}>
              תרומות, קמפיינים, CRM, דיוור, תקציב ובינה מלאכותית — במקום שבע מערכות ואקסל אחד עצבני, מערכת אחת שנבנתה לעמותות ישראליות.
            </p>
            <div className="d-rise d-d3 mt-10 flex flex-wrap items-center gap-5" style={{ fontFamily: 'Heebo, sans-serif' }}>
              <Link
                to="/register"
                className="px-8 py-4 text-lg font-bold border-2 transition-all duration-200 hover:-translate-y-0.5"
                style={{ background: RED, borderColor: INK, color: CREAM, boxShadow: `5px 5px 0 ${INK}` }}
              >
                פתח חשבון בחינם ←
              </Link>
              <span className="text-sm" style={{ color: '#6b645d' }}>7 ימי ניסיון · בלי כרטיס אשראי</span>
            </div>
          </div>

          {/* dateline column */}
          <aside className="hidden lg:flex flex-col gap-4 border-r-2 pr-6 pt-2 text-sm leading-relaxed w-52" style={{ borderColor: INK, fontFamily: 'Heebo, sans-serif', color: '#4a4540' }}>
            <p><b style={{ color: INK }}>בפנים:</b></p>
            <p>· סליקה מאובטחת (טרנזילה)</p>
            <p>· קבלות אוטומטיות</p>
            <p>· קמפיינים עם AI</p>
            <p>· תקציב מול ביצוע</p>
            <p>· דיוור, SMS ו-WhatsApp</p>
            <p className="pt-2 border-t" style={{ borderColor: '#d8cfc2' }}>🇮🇱 מיוצר בישראל</p>
          </aside>
        </div>
      </section>

      {/* ━━━━ TICKER ━━━━ */}
      <div className="border-y-2 py-3 overflow-hidden select-none" style={{ borderColor: INK }} dir="ltr">
        <div className="flex w-max whitespace-nowrap" style={{ animation: 'dTicker 26s linear infinite' }}>
          {[...TICKER, ...TICKER].map((w, i) => (
            <span key={i} className="mx-4 text-3xl" style={{ fontFamily: DISPLAY, WebkitTextStroke: i % 2 ? `1.5px ${INK}` : undefined, color: i % 2 ? 'transparent' : INK }}>
              {w} <span style={{ color: RED, WebkitTextStroke: '0px' }}>✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* ━━━━ PRINTS — real screenshots as taped photos ━━━━ */}
      <section ref={prints.ref} className="relative px-6 py-24">
        <div className="max-w-6xl mx-auto">
          <h2 className={`mb-3 transition-all duration-700 ${prints.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ fontFamily: DISPLAY, fontSize: 'clamp(32px, 5vw, 56px)' }}>
            מוצג א׳: המספרים האמיתיים
          </h2>
          <p className={`mb-14 text-lg transition-all duration-700 delay-100 ${prints.visible ? 'opacity-100' : 'opacity-0'}`}
             style={{ fontFamily: 'Heebo, sans-serif', color: '#4a4540' }}>
            צילומי מסך מתוך המערכת. בלי פוטושופ, בלי הבטחות באוויר.
          </p>

          <div className="relative">
            {/* main print */}
            <div className={`d-print relative bg-white p-3 pb-14 transition-all duration-1000 ${prints.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-14'}`}
                 style={{ transform: 'rotate(-1.2deg)', boxShadow: '0 18px 45px -15px rgba(22,20,18,0.3)', border: '1px solid #eee' }}>
              <Tape style={{ top: -14, right: 60 }} />
              <Tape style={{ top: -14, left: 60, transform: 'rotate(5deg)' }} />
              <img src="/screenshots/income-hero.png" alt="מסך הכנסות ותרומות — עמוטק" loading="lazy" className="w-full h-auto block" />
              <p className="absolute bottom-4 right-4 text-sm" style={{ fontFamily: 'Heebo, sans-serif', color: '#6b645d' }}>
                איור 1 — ₪307,547 תרומות מנוהלות במסך אחד. <b style={{ color: RED }}>אמיתי.</b>
              </p>
            </div>

            {/* two smaller prints, overlapping */}
            <div className="grid md:grid-cols-2 gap-10 mt-14 md:-mt-6 md:px-10">
              <div className={`d-print relative bg-white p-3 pb-12 transition-all duration-1000 delay-200 ${prints.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-14'}`}
                   style={{ transform: 'rotate(1.8deg)', boxShadow: '0 14px 35px -12px rgba(22,20,18,0.28)', border: '1px solid #eee' }}>
                <Tape style={{ top: -14, right: '38%', transform: 'rotate(2deg)' }} />
                <img src="/screenshots/budget.png" alt="לוח תקציב שנתי — עמוטק" loading="lazy" className="w-full h-auto block" />
                <p className="absolute bottom-3 right-4 text-sm" style={{ fontFamily: 'Heebo, sans-serif', color: '#6b645d' }}>
                  איור 2 — תקציב שנתי, סעיף־סעיף.
                </p>
              </div>
              <div className={`d-print relative bg-white p-3 pb-12 transition-all duration-1000 delay-300 ${prints.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-14'}`}
                   style={{ transform: 'rotate(-2.2deg)', boxShadow: '0 14px 35px -12px rgba(22,20,18,0.28)', border: '1px solid #eee' }}>
                <Tape style={{ top: -14, left: '38%', transform: 'rotate(-6deg)' }} />
                <img src="/screenshots/money-flow.png" alt="תנועת כספים — עמוטק" loading="lazy" className="w-full h-auto block" />
                <p className="absolute bottom-3 right-4 text-sm" style={{ fontFamily: 'Heebo, sans-serif', color: '#6b645d' }}>
                  איור 3 — תנועת כספים, 90 יום אחורה.
                </p>
              </div>
            </div>

            {/* three mini prints — telephony, mailing, calendar */}
            <div className="grid sm:grid-cols-3 gap-8 mt-16 md:px-4">
              {[
                { src: '/screenshots/mail-builder.png', alt: 'בונה הדיוור — עמוטק', cap: 'איור 4 — בונה הדיוור.', r: -1.6 },
                { src: '/screenshots/telephony.png', alt: 'מוקד טלפוני — עמוטק', cap: 'איור 5 — המוקד הטלפוני.', r: 1.8 },
                { src: '/screenshots/calendar.png', alt: 'לוח שנה — עמוטק', cap: 'איור 6 — לוח השנה.', r: -2 },
              ].map((s, i) => (
                <div key={s.src}
                     className={`d-print relative bg-white p-2.5 pb-10 transition-all duration-1000 ${prints.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-14'}`}
                     style={{ transform: `rotate(${s.r}deg)`, boxShadow: '0 12px 28px -10px rgba(22,20,18,0.25)', border: '1px solid #eee', transitionDelay: `${400 + i * 150}ms` }}>
                  <Tape style={{ top: -12, right: '38%', width: 80, height: 22, transform: `rotate(${i % 2 ? 4 : -4}deg)` }} />
                  <div style={{ height: 150, overflow: 'hidden' }}>
                    <img src={s.src} alt={s.alt} loading="lazy" className="w-full h-full block object-cover object-top" />
                  </div>
                  <p className="absolute bottom-2.5 right-3 text-xs" style={{ fontFamily: 'Heebo, sans-serif', color: '#6b645d' }}>{s.cap}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ━━━━ THREE EDITORIAL ROWS ━━━━ */}
      <section ref={steps.ref} className="px-6 pb-24">
        <div className="max-w-5xl mx-auto">
          {[
            { n: '01', t: 'מחברים סליקה', d: 'טרנזילה מתחברת בפחות מ-5 דקות. כרטיסי אשראי, ביט והוראות קבע — קבלה אוטומטית לכל תרומה.' },
            { n: '02', t: 'בונים קמפיין עם AI', d: 'דף גיוס שלם — טקסט, עיצוב וטופס — בכמה דקות. שגרירים, מד התקדמות ושיתוף בוואטסאפ.' },
            { n: '03', t: 'רואים הכל. שולטים בהכל.', d: 'דשבורד חי: מי תרם, כמה נשאר ליעד, תקציב מול ביצוע — והכל מתעדכן לבד.' },
          ].map((row, i) => (
            <div key={row.n}
                 className={`grid grid-cols-[auto_1fr] gap-6 sm:gap-10 items-baseline py-10 border-b-2 transition-all duration-700 ${steps.visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
                 style={{ borderColor: i === 2 ? INK : '#d8cfc2', transitionDelay: `${i * 180}ms` }}>
              <span className="text-6xl sm:text-7xl" style={{ fontFamily: DISPLAY, color: RED }}>{row.n}</span>
              <div>
                <h3 className="text-2xl sm:text-3xl mb-2" style={{ fontFamily: DISPLAY }}>{row.t}</h3>
                <p className="text-lg leading-relaxed" style={{ fontFamily: 'Heebo, sans-serif', color: '#4a4540' }}>{row.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ━━━━ CTA — ink band ━━━━ */}
      <section ref={cta.ref} className="px-6 py-24" style={{ background: INK, color: CREAM }}>
        <div className={`max-w-4xl mx-auto text-center transition-all duration-700 ${cta.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 style={{ fontFamily: DISPLAY, fontSize: 'clamp(36px, 6vw, 72px)', lineHeight: 1.1 }}>
            הגיליון הבא נכתב
            <br />
            <span style={{ color: '#F2B33D' }}>אצלכם בעמותה.</span>
          </h2>
          <p className="mt-6 text-lg" style={{ fontFamily: 'Heebo, sans-serif', color: '#b8b0a4' }}>
            7 ימי ניסיון חינם · ללא כרטיס אשראי · ביטול בכל עת
          </p>
          <Link
            to="/register"
            className="inline-block mt-10 px-10 py-4 text-xl font-bold border-2 transition-all duration-200 hover:-translate-y-1"
            style={{ fontFamily: 'Heebo, sans-serif', background: RED, borderColor: CREAM, color: CREAM, boxShadow: `6px 6px 0 ${CREAM}` }}
          >
            פתח חשבון בחינם ←
          </Link>
        </div>
      </section>

      <footer className="px-6 py-6 text-sm flex flex-col sm:flex-row items-center justify-between gap-2 max-w-6xl mx-auto" style={{ fontFamily: 'Heebo, sans-serif', color: '#6b645d' }}>
        <p>© 2026 עמוטק · Y.Sh כלכלה וצריכה חברתית בע״מ</p>
        <div className="flex gap-4">
          <Link to="/terms" className="hover:underline">תנאי שימוש</Link>
          <Link to="/privacy" className="hover:underline">פרטיות</Link>
          <Link to="/accessibility" className="hover:underline">נגישות</Link>
        </div>
      </footer>
    </div>
  );
};

export default LandingVariantD;
